import { type Job, type SourceMeta } from '@cibli/shared';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { JobCard } from '@/components/molecules/JobCard';
import { CloseTypeEnum } from '@/features/common/components/Header';
import IconButton from '@/features/common/components/IconButton';
import MainLayout from '@/features/common/components/MainLayout';
import LoadingScreen from '@/features/common/pages/LoadingScreen.tsx';
import NoJobsScreen from '@/features/matching/pages/NoJobsScreen.tsx';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useJobListedTracking } from '@/lib/analytics/use-job-listed-tracking';

/** Loading screen shows at least 3s, then stays until jobs are loaded */
const LOADING_MIN_MS = 3_000;

interface Props {
  jobs: Job[];
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  loading: boolean;
  streamedJobs?: Job[];
  onLoadMore: () => void;
  onJobClick: (job: Job) => void;
  onCancel: () => void;
  onBack: () => void;
  onAskedToBeContacted?: () => void;
  hasAppliedToJob: (job: Job) => boolean;
  hasSourceError?: boolean;
  sourcesMeta?: SourceMeta[];
  minTimePassed: boolean;
  onMinTimePassed: () => void;
}

export default function JobsListScreen({
  jobs,
  total,
  hasMore,
  loadingMore,
  loading,
  streamedJobs,
  onLoadMore,
  onJobClick,
  onCancel,
  onBack,
  onAskedToBeContacted,
  hasAppliedToJob,
  hasSourceError,
  minTimePassed,
  onMinTimePassed,
}: Props) {
  const { t } = useTranslation();
  const { getRef: getListedRef, flush: flushListed } = useJobListedTracking(jobs);

  const { observerRef } = useInfiniteScroll({
    onLoadMore,
    hasMore,
    isLoading: loadingMore,
  });

  // Min timer: after LOADING_MIN_MS, mark minTimePassed
  useEffect(() => {
    const timer = setTimeout(() => onMinTimePassed(), LOADING_MIN_MS);
    return () => clearTimeout(timer);
  }, [onMinTimePassed]);

  const showLoading = loading || !minTimePassed;

  if (showLoading) {
    return <LoadingScreen message={t('MatchingJobs.loading')} streamedJobs={streamedJobs} />;
  }

  if (jobs.length === 0) {
    return (
      <NoJobsScreen
        onCancel={onCancel}
        onBack={onBack}
        onAskedToBeContacted={onAskedToBeContacted}
      />
    );
  }

  return (
    <MainLayout
      align="top-left"
      className="gap-12 pb-40"
      closeType={CloseTypeEnum.End}
      onClose={onCancel}
      rightContent={
        <IconButton
          icon={<ArrowLeft strokeWidth={3} className="min-h-6 min-w-6" />}
          onClick={onBack}
        />
      }
    >
      <div className="text-xl md:text-2xl lg:text-3xl font-bold text-center">
        {t('MatchingJobs.title', { count: total })}
      </div>
      <div className="flex flex-col gap-4">
        {jobs.map((job, index) => (
          <JobCard
            key={`${job.id}-${index}`}
            ref={getListedRef(index)}
            job={job}
            onClick={() => {
              flushListed();
              onJobClick(job);
            }}
            hasApplied={hasAppliedToJob(job)}
          />
        ))}
        {hasSourceError && (
          <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {t('MatchingJobs.partialResults')}
          </div>
        )}
        {hasMore && <div ref={observerRef} className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-4">
            <Loader2 className="size-8 animate-spin text-primary" />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
